<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  StyleValue,
  watch,
  type HTMLAttributes,
} from 'vue';
import { inputContainerInjectionKey, labelInjectionKey } from './internal';

type InputContainerItemProps = {
  /** The text content of the label. */
  labelText: string;
  /** The unique identifier of the element associated with the label.
   *
   * **Do not provide this property!** The first slotted input element associates itself with the label if this property is omitted.
   *
   * If you want to overwrite this mechanism (there are cases where this actually makes sense), you are free to do so, but you have been warned. */
  for?: string;
  /** Whether this input container item should be hidden unless show more on the input container is active. Defaults to `false` */
  additionalItem?: boolean;
  /** Additional styling for the input container item element if necessary. Defaults to `undefined` */
  itemStyle?: StyleValue;
  /** Fill a bounded column while keeping the caption at its natural height. */
  fill?: boolean;
  /**
   * How many grid columns this item spans in the reactive `NxInputContainer`
   * grid. `'all'` spans the full row (e.g. a textarea / notes field). Spans
   * are clamped by the available column count. Defaults to `1`.
   */
  colSpan?: 1 | 2 | 'all';
  /**
   * Where the caption stands. `'top'` (default): above the input. `'inline'`:
   * on the control's line — the caption at the start, the control at the end
   * of the cell, both vertically centred, the row one input tall and on the
   * line of the inputs beside it (a settings-list row, I-2955). Meant for a
   * control narrower than the cell, such as `NxSwitch`. The caption stays tied
   * to the control (a click on it toggles, it names the control), the
   * `label-suffix` slot stays beside it, and a caption that does not fit is
   * clipped like the stacked one.
   */
  labelPlacement?: 'top' | 'inline';
};

const props = defineProps<InputContainerItemProps & /* @vue-ignore */ HTMLAttributes>();

// The label is clipped to one line (see the style block), so a caption that does
// not fit needs a way to be read in full — that is what `title` is for. It is set
// ONLY when the text is really cut off: an unconditional title puts a native
// tooltip on EVERY label in every mask that just repeats the visible caption,
// which is noise and hides the case that matters (review of #1339).
const labelEl = ref<HTMLLabelElement>();
const isLabelClipped = ref(false);

const measureLabel = () => {
  const el = labelEl.value;
  // +1 absorbs sub-pixel rounding: a label that fits exactly reports scrollWidth
  // one off on fractional zoom levels.
  isLabelClipped.value = !!el && el.scrollWidth > el.clientWidth + 1;
};

const labelTitle = computed(() => (isLabelClipped.value ? props.labelText : undefined));

let observer: ResizeObserver | undefined;
onMounted(() => {
  measureLabel();
  // Clipping depends on the COLUMN width, which changes with the viewport, the
  // density preset and a sibling's content — none of which this component sees.
  // Guarded because jsdom (and older embedders) have no ResizeObserver.
  if (typeof ResizeObserver !== 'undefined' && labelEl.value) {
    observer = new ResizeObserver(measureLabel);
    observer.observe(labelEl.value);
  }
});
onBeforeUnmount(() => observer?.disconnect());
watch(
  () => props.labelText,
  () => nextTick(measureLabel)
);

// Grid placement for this item within NxInputContainer's auto-fit grid.
// 'all' → full row; 2 → two tracks; 1 (default) → a single cell (no override).
const gridColumnStyle = computed<StyleValue>(() => {
  if (props.colSpan === 'all') return { gridColumn: '1 / -1' };
  if (props.colSpan === 2) return { gridColumn: 'span 2' };
  return {};
});

// Attempt to retrieve the input container injection
const inputContainerInjection = inject(inputContainerInjectionKey);
// Define a computed ref to determine whether the item should be shown right now
const showItem = computed(() => {
  // If the input container injection is not defined, the additional item is never shown as we can't retrieve whether more items should be shown
  return !props.additionalItem || inputContainerInjection?.showMore.value;
});

// Assign the for property to a reactive object. The reactive object is assigned to the for attribute of the label
const labelFor = ref<string | undefined>(props.for);
// The label injection is used to associate the first slotted input element with the label if the label isn't associated with another element yet
const setLabelFor = (elementId: string) => {
  // The function should only be called after verifying no element has been associated yet but better be safe than sorry
  labelFor.value ??= elementId;
};
provide(labelInjectionKey, { for: labelFor, setFor: setLabelFor });
</script>
<template>
  <div
    v-show="showItem"
    :style="[gridColumnStyle, $props.itemStyle]"
    class="input-container-item"
    :class="{ 'label-inline': labelPlacement === 'inline', 'is-fill': props.fill }"
    :data-label-placement="labelPlacement ?? 'top'"
  >
    <!-- The suffix sits BESIDE the label, never inside it: a control nested in a
         <label> forwards its clicks to the associated input, so an info icon in
         there would focus the field on every click. -->
    <div class="label-row">
      <!-- `title` ONLY when the caption is actually clipped — see labelTitle. -->
      <label ref="labelEl" :for="labelFor" :title="labelTitle" v-text="$props.labelText" />
      <slot name="label-suffix" />
    </div>
    <div><slot /></div>
  </div>
</template>
<style scoped>
.input-container-item {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);

  .label-row {
    display: flex;
    align-items: center;
    gap: var(--nxt-space-2xs);
    /* Reserve one line even when the label is empty (a label-less group column),
       so its input stays aligned with labelled sibling columns. */
    min-height: 1lh;
    /* Let the label shrink instead of widening the grid column. */
    min-width: 0;
  }

  /* A small heading like NxLabel: the design's caps axis (data-nx-caps,
     I-2510) sets it in small capitals; by default it stays as written. */
  label {
    font-size: var(--nxt-label-size);
    color: var(--nxt-label-fg);
    letter-spacing: var(--nxd-caption-tracking, normal);
    text-transform: var(--nxd-caption-transform, none);
    /* ONE line, always. A caption that wrapped made its own input start a line
       lower than its neighbours, so a single long label pushed one box out of
       the row (I-1249 review). Clipping keeps every input on the same baseline;
       the full text stays reachable through the `title`. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  /* Whatever sits beside the label keeps its size — only the caption gives way.
     It is also forced into a centred flex box: a suffix that is an inline-block
     (NxTooltip's wrapper is one) gets a line box taller than its own content, and
     its icon then hangs at the top of that box instead of beside the caption. */
  .label-row > :not(label) {
    flex: none;
    display: flex;
    align-items: center;
  }

  & > :last-child {
    width: 100%; /* input fills the column */
  }
}

.input-container-item.is-fill {
  flex: 1 1 auto;
  min-height: 0;

  .label-row {
    flex: none;
  }

  & > :last-child {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* `labelPlacement: 'inline'` (I-2955): caption and control on ONE line, the
   caption at the start, the control at the end of the cell, both centred. */
.input-container-item.label-inline {
  flex-direction: row;
  align-items: center;
  gap: var(--nxt-space-xs);
  /* One input box tall (Inputs/style.css, height `s`): a line of the control
     line height at the input font size, plus the em-based block padding
     (2 × 0.5em × space scale) and the hairline border on both sides. Written
     out in the input's units, so this cell needs no font size of its own. */
  min-height: calc(
    var(--nxt-font-size-sm) * (var(--nxt-line-height-control) + var(--nxt-space-scale)) + 2 *
      var(--nxt-hairline)
  );
  /* On the INPUTS' line, not the captions': in a grid row with stacked
     neighbours (caption above input) the free space goes above this row, so
     the switch stands level with the inputs beside it. An auto margin, not
     `align-self: end` — in a flex column that would move the item sideways. */
  margin-block-start: auto;

  /* The caption takes the room; it clips like the stacked one. */
  .label-row {
    flex: 1 1 auto;
  }

  /* The control keeps its own width at the cell's end. */
  & > :last-child {
    flex: none;
    width: auto;
    display: flex;
    align-items: center;
  }
}
</style>
